import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EvaluationStandardDraft } from '../../types/evaluationPlan';
import {
  EMPTY_EVALUATION_LEVELS,
  compareStandardsByCode,
  createEmptyStandardDraft,
  type EvaluationLevelDisplayLabels,
  type EvaluationLevelKey,
  type EvaluationLevelStepCount,
  getActiveLevelKeys,
  getActiveLevelLabels,
} from '../../types/evaluationPlan';
import {
  bulkTableCellKey,
  bulkTableCellNeighborKeys,
  expandBulkSelectionToFullRows,
  parseBulkTableCellKey,
  parseBulkTablePasteGrid,
  rectBulkTableCellKeys,
  shouldHandleBulkTablePaste,
} from '../../utils/evaluationPlanBulkTable';
import {
  extractAchievementCode,
  pickDefaultSheetName,
  pickDefaultTrackFilter,
  readStandardSheetsFromFile,
  type ImportedSheet,
  type ImportedStandardRow,
} from '../../utils/evaluationStandardFileImport';
import AutoStatusToast from '../common/AutoStatusToast';
import { useAutoStatusToast } from '../../hooks/useAutoStatusToast';

type BulkColumnKey = 'track_text' | 'area_text' | 'achievement_text' | EvaluationLevelKey;

const BASE_COLUMNS: Array<{ key: BulkColumnKey; label: string; wide?: boolean }> = [
  { key: 'track_text', label: '편제' },
  { key: 'area_text', label: '영역' },
  { key: 'achievement_text', label: '성취기준', wide: true },
];

const LEVEL_COLUMNS: Array<{ key: EvaluationLevelKey; label: string; wide?: boolean }> = [
  { key: 'level_a', label: '수준 A', wide: true },
  { key: 'level_b', label: '수준 B', wide: true },
  { key: 'level_c', label: '수준 C', wide: true },
  { key: 'level_d', label: '수준 D', wide: true },
  { key: 'level_e', label: '수준 E', wide: true },
];

function levelColumnLabel(key: EvaluationLevelKey, levelStepCount: EvaluationLevelStepCount, displayLabels?: EvaluationLevelDisplayLabels | null): string {
  const idx = getActiveLevelKeys(5).indexOf(key);
  if (idx < 0) return `수준 ${key.replace('level_', '').toUpperCase()}`;
  const labels = getActiveLevelLabels(levelStepCount, displayLabels);
  return `수준 ${labels[idx] ?? key.replace('level_', '').toUpperCase()}`;
}

function cloneRows(rows: EvaluationStandardDraft[]): EvaluationStandardDraft[] {
  return rows.map((row) => ({ ...row, ...EMPTY_EVALUATION_LEVELS, ...row }));
}

/** 모든 칸을 자동 높이 textarea 로 — 긴 편제·영역도 잘리지 않게 */
function isMultilineColumn(_key: BulkColumnKey): boolean {
  return true;
}

const SUPPORTS_FIELD_SIZING =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('field-sizing', 'content');

function autosizeTextarea(el: HTMLTextAreaElement) {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

const BULK_TRACK_AREA_WIDTH_PCT = 5;
const BULK_ACHIEVEMENT_WIDTH_PCT = 27;

function bulkColumnCellClass(key: BulkColumnKey): string {
  if (key === 'track_text' || key === 'area_text') return 'is-col-narrow';
  if (key === 'achievement_text') return 'is-col-achievement';
  return 'is-col-level';
}

function bulkColumnWidthPct(key: BulkColumnKey, levelColumnCount: number): string {
  if (key === 'track_text' || key === 'area_text') return `${BULK_TRACK_AREA_WIDTH_PCT}%`;
  if (key === 'achievement_text') return `${BULK_ACHIEVEMENT_WIDTH_PCT}%`;
  const rest = 100 - BULK_TRACK_AREA_WIDTH_PCT * 2 - BULK_ACHIEVEMENT_WIDTH_PCT;
  return `${rest / Math.max(levelColumnCount, 1)}%`;
}

const EvaluationStandardBulkEditModal: React.FC<{
  open: boolean;
  categoryName: string;
  standards: EvaluationStandardDraft[];
  levelStepCount: EvaluationLevelStepCount;
  levelDisplayLabels?: EvaluationLevelDisplayLabels | null;
  /** 파일 업로드 시 기본 시트(학년군) 선택용 — 현재 클래스 학년 */
  classGradeNumbers?: number[];
  onClose: () => void;
  onApply: (standards: EvaluationStandardDraft[]) => void;
}> = ({ open, categoryName, standards, levelStepCount, levelDisplayLabels, classGradeNumbers, onClose, onApply }) => {
  const [draftRows, setDraftRows] = useState<EvaluationStandardDraft[]>([]);
  /** 파일 업로드: 시트/편제 선택 + 추가/교체 확인 단계 */
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importState, setImportState] = useState<{
    fileName: string;
    sheets: ImportedSheet[];
    selectedSheets: string[];
    trackFilter: string;
    mode: 'append' | 'replace';
  } | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const { toastState, showError, showSuccess } = useAutoStatusToast();
  const tableRef = useRef<HTMLTableElement | null>(null);

  /**
   * field-sizing 미지원 브라우저용 자동 높이: rAF 한 번에 모든 textarea 를
   * (1) height:auto 일괄 쓰기 → (2) scrollHeight 일괄 읽기 → (3) 일괄 쓰기 (레이아웃 스래싱 방지, 600+행 대응)
   */
  const resizeAllRafRef = useRef<number | null>(null);
  const scheduleResizeAll = useCallback(() => {
    if (SUPPORTS_FIELD_SIZING) return;
    if (resizeAllRafRef.current != null) cancelAnimationFrame(resizeAllRafRef.current);
    resizeAllRafRef.current = requestAnimationFrame(() => {
      resizeAllRafRef.current = null;
      const table = tableRef.current;
      if (!table) return;
      const areas = Array.from(table.querySelectorAll<HTMLTextAreaElement>('textarea.cp-evaluation-plan-bulk-cell-input'));
      areas.forEach((el) => {
        el.style.height = 'auto';
      });
      const heights = areas.map((el) => el.scrollHeight);
      areas.forEach((el, i) => {
        el.style.height = `${heights[i]}px`;
      });
    });
  }, []);
  const [selectedCells, setSelectedCells] = useState<string[]>([]);
  const draftRowsRef = useRef(draftRows);
  const selectedCellsRef = useRef(selectedCells);
  const selectionAnchorRef = useRef<{ rowIdx: number; colKey: BulkColumnKey } | null>(null);
  const cellDragStateRef = useRef<{
    active: boolean;
    anchorRowIdx: number;
    anchorColKey: BulkColumnKey;
    currentRowIdx: number;
    currentColKey: BulkColumnKey;
  } | null>(null);

  draftRowsRef.current = draftRows;
  selectedCellsRef.current = selectedCells;

  const columns = useMemo(() => {
    const activeLevelKeys = new Set(getActiveLevelKeys(levelStepCount));
    const levelCols = LEVEL_COLUMNS.filter((col) => activeLevelKeys.has(col.key)).map((col) => ({
      ...col,
      label: levelColumnLabel(col.key, levelStepCount, levelDisplayLabels),
    }));
    return [...BASE_COLUMNS, ...levelCols];
  }, [levelStepCount, levelDisplayLabels]);

  const columnKeys = useMemo(() => columns.map((col) => col.key), [columns]);
  const levelColumnCount = useMemo(() => columns.filter((col) => col.key.startsWith('level_')).length, [columns]);
  const selectedCellSet = useMemo(() => new Set(selectedCells), [selectedCells]);

  useEffect(() => {
    if (!open) return;
    setDraftRows(cloneRows([...standards].sort(compareStandardsByCode)));
    setSelectedCells([]);
    selectionAnchorRef.current = null;
    cellDragStateRef.current = null;
  }, [open, standards]);

  const focusCellInput = useCallback((rowId: string, colKey: BulkColumnKey) => {
    const cellKey = bulkTableCellKey(rowId, colKey);
    const td = document.querySelector<HTMLElement>(`[data-bulk-cell-key="${cellKey}"]`);
    const input = td?.querySelector('input, textarea');
    if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
      input.focus();
      if (input instanceof HTMLInputElement) input.select();
    }
  }, []);

  const handleCellMouseDown = useCallback(
    (rowIdx: number, colKey: BulkColumnKey, e: React.MouseEvent<HTMLTableCellElement>) => {
      if (e.button !== 0) return;
      const row = draftRowsRef.current[rowIdx];
      if (!row) return;

      const cellKey = bulkTableCellKey(row.id, colKey);
      const isInput = Boolean((e.target as HTMLElement).closest('input, textarea'));

      if (e.shiftKey && selectionAnchorRef.current) {
        const keys = expandBulkSelectionToFullRows(
          rectBulkTableCellKeys(
            draftRowsRef.current,
            columnKeys,
            selectionAnchorRef.current,
            { rowIdx, colKey }
          ),
          draftRowsRef.current,
          columnKeys
        );
        cellDragStateRef.current = null;
        setSelectedCells(keys);
        return;
      }

      const alreadyOnlySelected =
        selectedCellsRef.current.length === 1 &&
        selectedCellsRef.current[0] === cellKey &&
        !cellDragStateRef.current?.active &&
        isInput;
      if (alreadyOnlySelected) return;

      if (!isInput) e.preventDefault();

      selectionAnchorRef.current = { rowIdx, colKey };
      cellDragStateRef.current = {
        active: true,
        anchorRowIdx: rowIdx,
        anchorColKey: colKey,
        currentRowIdx: rowIdx,
        currentColKey: colKey,
      };
      setSelectedCells([cellKey]);
      focusCellInput(row.id, colKey);
    },
    [columnKeys, focusCellInput]
  );

  const handleCellMouseEnter = useCallback(
    (rowIdx: number, colKey: BulkColumnKey) => {
      const state = cellDragStateRef.current;
      if (!state?.active) return;
      const keys = expandBulkSelectionToFullRows(
        rectBulkTableCellKeys(
          draftRowsRef.current,
          columnKeys,
          { rowIdx: state.anchorRowIdx, colKey: state.anchorColKey },
          { rowIdx, colKey }
        ),
        draftRowsRef.current,
        columnKeys
      );
      cellDragStateRef.current = { ...state, currentRowIdx: rowIdx, currentColKey: colKey };
      setSelectedCells(keys);
    },
    [columnKeys]
  );

  useEffect(() => {
    const onMouseUp = () => {
      if (cellDragStateRef.current) {
        cellDragStateRef.current = { ...cellDragStateRef.current, active: false };
      }
    };
    document.addEventListener('mouseup', onMouseUp, true);
    return () => document.removeEventListener('mouseup', onMouseUp, true);
  }, []);

  const selectedRowIds = useMemo(() => {
    const ids = new Set<string>();
    for (const cellKey of selectedCells) {
      const parsed = parseBulkTableCellKey(cellKey);
      if (parsed) ids.add(parsed.rowId);
    }
    return ids;
  }, [selectedCells]);

  const hasSelectedRows = selectedRowIds.size > 0;

  const handleAddRow = () => {
    setDraftRows((prev) => [...prev, createEmptyStandardDraft(categoryName, prev.length)]);
  };

  const handleDeleteRows = () => {
    if (!hasSelectedRows) return;
    const ids = new Set(selectedRowIds);
    setDraftRows((prev) => prev.filter((row) => !ids.has(row.id)).map((row, idx) => ({ ...row, sort_order: idx })));
    setSelectedCells([]);
    selectionAnchorRef.current = null;
  };

  const handleMoveRows = (direction: -1 | 1) => {
    if (!hasSelectedRows) return;
    setDraftRows((prev) => {
      const ordered = [...prev];
      const selectedIds = new Set<string>();
      for (const cellKey of selectedCellsRef.current) {
        const parsed = parseBulkTableCellKey(cellKey);
        if (parsed) selectedIds.add(parsed.rowId);
      }
      const selectedIndices = ordered
        .map((row, idx) => (selectedIds.has(row.id) ? idx : -1))
        .filter((idx) => idx >= 0)
        .sort((a, b) => a - b);
      if (!selectedIndices.length) return prev;

      const minIdx = selectedIndices[0];
      const maxIdx = selectedIndices[selectedIndices.length - 1];

      let next: EvaluationStandardDraft[];
      if (direction < 0) {
        if (minIdx <= 0) return prev;
        const pivot = ordered[minIdx - 1];
        const before = ordered.slice(0, minIdx - 1);
        const block = ordered.slice(minIdx, maxIdx + 1);
        const after = ordered.slice(maxIdx + 1);
        next = [...before, ...block, pivot, ...after];
      } else {
        if (maxIdx >= ordered.length - 1) return prev;
        const pivot = ordered[maxIdx + 1];
        const before = ordered.slice(0, minIdx);
        const block = ordered.slice(minIdx, maxIdx + 1);
        const after = ordered.slice(maxIdx + 2);
        next = [...before, pivot, ...block, ...after];
      }
      return next.map((row, idx) => ({ ...row, sort_order: idx }));
    });
  };

  const patchCell = (rowId: string, key: BulkColumnKey, value: string) => {
    setDraftRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, [key]: value } : row)));
  };

  const handleCellPaste = useCallback(
    (rowIndex: number, colKey: BulkColumnKey) => (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const text = e.clipboardData.getData('text/plain');
      if (!shouldHandleBulkTablePaste(text)) return;
      e.preventDefault();
      const grid = parseBulkTablePasteGrid(text);
      if (!grid.length) return;

      const pasteableCols = columnKeys;
      const startColIndex = pasteableCols.indexOf(colKey);
      if (startColIndex < 0) return;

      setDraftRows((prev) => {
        const ordered = [...prev];
        const next = [...prev];
        const patchById = new Map<string, Partial<EvaluationStandardDraft>>();

        for (let ri = 0; ri < grid.length; ri += 1) {
          const targetIdx = rowIndex + ri;
          let row = ordered[targetIdx];
          if (!row) {
            row = createEmptyStandardDraft(categoryName, next.length);
            ordered.push(row);
            next.push(row);
          }
          let patch = patchById.get(row.id) ?? {};
          for (let ci = 0; ci < grid[ri].length; ci += 1) {
            const colIdx = startColIndex + ci;
            if (colIdx >= pasteableCols.length) break;
            const key = pasteableCols[colIdx] as BulkColumnKey;
            patch = { ...patch, [key]: grid[ri][ci] ?? '' };
          }
          patchById.set(row.id, patch);
        }

        if (patchById.size === 0) return prev;
        return next.map((row) => (patchById.has(row.id) ? { ...row, ...patchById.get(row.id)! } : row));
      });
      scheduleResizeAll();
    },
    [categoryName, columnKeys, scheduleResizeAll]
  );

  useEffect(() => {
    if (!open) return;
    scheduleResizeAll();
  }, [open, draftRows.length, columns, scheduleResizeAll]);

  useEffect(() => {
    if (!open || SUPPORTS_FIELD_SIZING) return;
    const onResize = () => scheduleResizeAll();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (resizeAllRafRef.current != null) cancelAnimationFrame(resizeAllRafRef.current);
    };
  }, [open, scheduleResizeAll]);

  const hasExistingContent = useMemo(
    () =>
      draftRows.some((row) =>
        [row.track_text, row.area_text, row.achievement_text, ...getActiveLevelKeys(5).map((k) => row[k])].some(
          (v) => String(v ?? '').trim().length > 0
        )
      ),
    [draftRows]
  );

  const handleFileChosen = async (file: File | undefined) => {
    if (!file) return;
    setImportLoading(true);
    try {
      const sheets = await readStandardSheetsFromFile(file);
      const defaultSheet = pickDefaultSheetName(sheets, classGradeNumbers ?? []);
      if (!defaultSheet) {
        showError('파일에서 성취기준 행을 찾지 못했어요. 편제·영역·성취기준·수준 열을 확인해 주세요.');
        return;
      }
      const tracks = Array.from(
        new Set((sheets.find((s) => s.name === defaultSheet)?.rows ?? []).map((r) => r.track).filter(Boolean))
      );
      setImportState({
        fileName: file.name,
        sheets,
        selectedSheets: [defaultSheet],
        trackFilter: pickDefaultTrackFilter(tracks, categoryName),
        mode: 'append',
      });
    } catch (err) {
      if (err instanceof Error && err.message === 'XLS_UNSUPPORTED') {
        showError('.xls 형식은 지원하지 않아요. 엑셀에서 .xlsx 로 저장한 뒤 올려 주세요.');
      } else {
        showError('파일을 읽지 못했어요. .xlsx 또는 .csv 파일인지 확인해 주세요.');
      }
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const importSelectedRows = useMemo((): ImportedStandardRow[] => {
    if (!importState) return [];
    const seen = new Set<string>();
    const out: ImportedStandardRow[] = [];
    importState.sheets
      .filter((s) => importState.selectedSheets.includes(s.name))
      .forEach((s) =>
        s.rows.forEach((r) => {
          if (importState.trackFilter && r.track !== importState.trackFilter) return;
          /** 여러 시트(예: 학년군 + 전체)를 함께 고르면 같은 코드 중복 제거 */
          const key = extractAchievementCode(r.achievement) || `${r.track}|${r.area}|${r.achievement}`;
          if (seen.has(key)) return;
          seen.add(key);
          out.push(r);
        })
      );
    return out;
  }, [importState]);

  const importTrackOptions = useMemo(() => {
    if (!importState) return [] as string[];
    const set = new Set<string>();
    importState.sheets
      .filter((s) => importState.selectedSheets.includes(s.name))
      .forEach((s) => s.rows.forEach((r) => r.track && set.add(r.track)));
    return Array.from(set);
  }, [importState]);

  const applyImport = () => {
    if (!importState || importSelectedRows.length === 0) return;
    const levelKeys = getActiveLevelKeys(5);
    const toPatch = (r: ImportedStandardRow): Partial<EvaluationStandardDraft> => {
      const patch: Partial<EvaluationStandardDraft> = {
        track_text: r.track,
        area_text: r.area,
        achievement_text: r.achievement,
      };
      r.levels.forEach((v, i) => {
        const key = levelKeys[i];
        if (key) (patch as Record<string, string>)[key] = v;
      });
      return patch;
    };
    if (importState.mode === 'replace' || !hasExistingContent) {
      const rows = importSelectedRows.map((r, idx) => ({
        ...createEmptyStandardDraft(categoryName, idx),
        ...toPatch(r),
        achievement_code: extractAchievementCode(r.achievement),
      }));
      setDraftRows(rows);
      showSuccess(
        importState.mode === 'replace' && hasExistingContent
          ? `${rows.length}개 행으로 교체했어요. [저장]을 눌러야 반영돼요.`
          : `추가 ${rows.length}개. [저장]을 눌러야 반영돼요.`
      );
    } else {
      let added = 0;
      let updated = 0;
      {
        const prev = draftRowsRef.current;
        const next = prev.filter((row) =>
          [row.track_text, row.area_text, row.achievement_text, ...levelKeys.map((k) => row[k])].some(
            (v) => String(v ?? '').trim().length > 0
          )
        );
        const indexByCode = new Map<string, number>();
        next.forEach((row, idx) => {
          const code = extractAchievementCode(row.achievement_text ?? '') || (row.achievement_code ?? '').replace(/[[\]\s]/g, '');
          if (code && !indexByCode.has(code)) indexByCode.set(code, idx);
        });
        importSelectedRows.forEach((r) => {
          const code = extractAchievementCode(r.achievement);
          const hit = code ? indexByCode.get(code) : undefined;
          if (hit !== undefined) {
            next[hit] = { ...next[hit], ...toPatch(r) };
            updated += 1;
          } else {
            next.push({ ...createEmptyStandardDraft(categoryName, next.length), ...toPatch(r), achievement_code: code });
            if (code) indexByCode.set(code, next.length - 1);
            added += 1;
          }
        });
        setDraftRows(next.map((row, idx) => ({ ...row, sort_order: idx })));
      }
      showSuccess(`추가 ${added}개, 갱신 ${updated}개. [저장]을 눌러야 반영돼요.`);
    }
    setSelectedCells([]);
    selectionAnchorRef.current = null;
    setImportState(null);
    scheduleResizeAll();
  };

  const renderCell = (row: EvaluationStandardDraft, rowIdx: number, col: (typeof columns)[number]) => {
    const cellKey = bulkTableCellKey(row.id, col.key);
    const isSelected = selectedCellSet.has(cellKey);
    const selectedSingle = isSelected && selectedCellSet.size === 1;
    const selectedMulti = isSelected && selectedCellSet.size > 1;
    const value = String(row[col.key] ?? '');
    const multiline = isMultilineColumn(col.key);

    let edgeTop = false;
    let edgeBottom = false;
    let edgeLeft = false;
    let edgeRight = false;
    if (selectedMulti) {
      const neighbors = bulkTableCellNeighborKeys(draftRows, columnKeys, rowIdx, col.key);
      edgeTop = !neighbors.top || !selectedCellSet.has(neighbors.top);
      edgeBottom = !neighbors.bottom || !selectedCellSet.has(neighbors.bottom);
      edgeLeft = !neighbors.left || !selectedCellSet.has(neighbors.left);
      edgeRight = !neighbors.right || !selectedCellSet.has(neighbors.right);
    }

    return (
      <td
        key={col.key}
        data-bulk-cell-key={cellKey}
        className={[
          'cp-evaluation-plan-bulk-td',
          bulkColumnCellClass(col.key),
          selectedSingle ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-single' : '',
          selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
          edgeTop ? 'cp-base-timetable-cell-edge-top' : '',
          edgeBottom ? 'cp-base-timetable-cell-edge-bottom' : '',
          edgeLeft ? 'cp-base-timetable-cell-edge-left' : '',
          edgeRight ? 'cp-base-timetable-cell-edge-right' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseDown={(e) => handleCellMouseDown(rowIdx, col.key, e)}
        onMouseEnter={() => handleCellMouseEnter(rowIdx, col.key)}
      >
        <div className="cp-evaluation-plan-bulk-cell-inner">
          {multiline ? (
            <textarea
              className="cp-evaluation-plan-bulk-cell-input cp-evaluation-plan-bulk-cell-input--multiline"
              rows={1}
              value={value}
              aria-label={col.label}
              onChange={(e) => {
                patchCell(row.id, col.key, e.target.value);
                if (!SUPPORTS_FIELD_SIZING) autosizeTextarea(e.currentTarget);
              }}
              onPaste={handleCellPaste(rowIdx, col.key)}
              onFocus={() => {
                selectionAnchorRef.current = { rowIdx, colKey: col.key };
                setSelectedCells([cellKey]);
              }}
            />
          ) : (
            <input
              type="text"
              className="cp-evaluation-plan-bulk-cell-input cp-evaluation-plan-bulk-cell-input--single"
              value={value}
              aria-label={col.label}
              onChange={(e) => patchCell(row.id, col.key, e.target.value)}
              onPaste={handleCellPaste(rowIdx, col.key)}
              onFocus={() => {
                selectionAnchorRef.current = { rowIdx, colKey: col.key };
                setSelectedCells([cellKey]);
              }}
            />
          )}
        </div>
      </td>
    );
  };

  if (!open) return null;

  return (
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        className="cp-modal cp-evaluation-plan-bulk-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-evaluation-plan-bulk-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-modal-document-header cp-evaluation-plan-bulk-header">
          <h3 id="cp-evaluation-plan-bulk-title">성취기준 일괄등록 및 수정</h3>
          <div className="cp-modal-document-actions">
            <button
              type="button"
              className="cp-evaluation-plan-bulk-upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={importLoading}
              title="엑셀(.xlsx) 또는 CSV 파일에서 성취기준을 불러옵니다"
            >
              {importLoading ? '읽는 중…' : '파일 업로드'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
              hidden
              onChange={(e) => void handleFileChosen(e.target.files?.[0])}
            />
          </div>
        </div>
        <p className="cp-evaluation-plan-bulk-hint" role="status">
          [저장]을 누르면 변경 내용이 바로 저장됩니다.
        </p>
        <div className="cp-program-progress-toolbar cp-evaluation-plan-bulk-toolbar">
          <div className="cp-program-progress-toolbar-group" role="group" aria-label="행 편집 도구">
            <button type="button" className="cp-btn-copy-paste cp-btn-swap-region cp-btn-plusminus" onClick={handleAddRow}>
              <span className="cp-btn-label-desktop">행 추가</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                +
              </span>
            </button>
            <button
              type="button"
              className="cp-btn-copy-paste cp-btn-swap-region cp-btn-plusminus"
              onClick={handleDeleteRows}
              disabled={!hasSelectedRows}
            >
              <span className="cp-btn-label-desktop">행 삭제</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                -
              </span>
            </button>
            <button
              type="button"
              className="cp-btn-copy-paste cp-btn-swap-region"
              onClick={() => handleMoveRows(-1)}
              disabled={!hasSelectedRows}
            >
              <span className="cp-btn-label-desktop">한 칸 올리기</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                ↑
              </span>
            </button>
            <button
              type="button"
              className="cp-btn-copy-paste cp-btn-swap-region"
              onClick={() => handleMoveRows(1)}
              disabled={!hasSelectedRows}
            >
              <span className="cp-btn-label-desktop">한 칸 내리기</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                ↓
              </span>
            </button>
          </div>
        </div>
        <div className="cp-modal-body cp-evaluation-plan-bulk-body">
          <div className="cp-evaluation-plan-bulk-table-wrap">
            <table ref={tableRef} className="cp-evaluation-plan-bulk-table">
              <colgroup>
                {columns.map((col) => (
                  <col
                    key={col.key}
                    className={`cp-evaluation-plan-bulk-col cp-evaluation-plan-bulk-col--${col.key}`}
                    style={{ width: bulkColumnWidthPct(col.key, levelColumnCount) }}
                  />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col.key} scope="col" className={bulkColumnCellClass(col.key)}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {draftRows.map((row, rowIdx) => (
                  <tr key={row.id}>{columns.map((col) => renderCell(row, rowIdx, col))}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            onClick={() => {
              onApply(draftRows.map((row, idx) => ({ ...row, sort_order: idx, category_name: categoryName })));
              onClose();
            }}
          >
            저장
          </button>
        </div>
      </div>
      {importState ? (
        <div
          className="cp-modal-backdrop cp-evaluation-plan-bulk-import-backdrop"
          role="presentation"
          onClick={(e) => {
            e.stopPropagation();
            setImportState(null);
          }}
        >
          <div
            className="cp-modal cp-evaluation-plan-bulk-import-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-evaluation-plan-bulk-import-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-evaluation-plan-bulk-import-title">파일에서 불러오기</h3>
            <div className="cp-modal-body cp-evaluation-plan-bulk-import-body">
              <p className="cp-evaluation-plan-bulk-import-file">{importState.fileName}</p>
              {importState.sheets.length > 1 ? (
                <div className="cp-evaluation-plan-bulk-import-field">
                  <span className="cp-evaluation-plan-bulk-import-label">시트</span>
                  <div className="cp-evaluation-plan-bulk-import-chips">
                    {importState.sheets.map((sheet) => {
                      const checked = importState.selectedSheets.includes(sheet.name);
                      const empty = sheet.rows.length === 0;
                      return (
                        <label
                          key={sheet.name}
                          className={`cp-evaluation-plan-bulk-import-chip${checked ? ' is-on' : ''}${empty ? ' is-disabled' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={empty}
                            onChange={() =>
                              setImportState((prev) => {
                                if (!prev) return prev;
                                const selectedSheets = checked
                                  ? prev.selectedSheets.filter((n) => n !== sheet.name)
                                  : [...prev.selectedSheets, sheet.name];
                                return { ...prev, selectedSheets, trackFilter: prev.trackFilter };
                              })
                            }
                          />
                          <span className="cp-evaluation-plan-bulk-import-chip-check" aria-hidden="true">
                            {checked ? '✓' : ''}
                          </span>
                          <span className="cp-evaluation-plan-bulk-import-chip-name">{sheet.name}</span>
                          <em>{empty ? '데이터 없음' : `${sheet.rows.length}행`}</em>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              <div className="cp-evaluation-plan-bulk-import-field">
                <span className="cp-evaluation-plan-bulk-import-label">편제</span>
                <select
                  className="cp-evaluation-plan-bulk-import-select"
                  value={importTrackOptions.includes(importState.trackFilter) ? importState.trackFilter : ''}
                  onChange={(e) => setImportState((prev) => (prev ? { ...prev, trackFilter: e.target.value } : prev))}
                >
                  <option value="">전체 편제</option>
                  {importTrackOptions.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              {hasExistingContent ? (
                <div className="cp-evaluation-plan-bulk-import-field">
                  <span className="cp-evaluation-plan-bulk-import-label">기존 행</span>
                  <div className="cp-evaluation-plan-bulk-import-modes" role="radiogroup">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={importState.mode === 'append'}
                      className={`cp-evaluation-plan-bulk-import-mode${importState.mode === 'append' ? ' is-on' : ''}`}
                      onClick={() => setImportState((prev) => (prev ? { ...prev, mode: 'append' } : prev))}
                    >
                      추가
                      <small>같은 코드는 갱신</small>
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={importState.mode === 'replace'}
                      className={`cp-evaluation-plan-bulk-import-mode is-danger${importState.mode === 'replace' ? ' is-on' : ''}`}
                      onClick={() => setImportState((prev) => (prev ? { ...prev, mode: 'replace' } : prev))}
                    >
                      교체
                      <small>기존 행은 저장 시 삭제</small>
                    </button>
                  </div>
                </div>
              ) : null}
              <p className="cp-evaluation-plan-bulk-import-summary">
                불러올 행 <b>{importSelectedRows.length}</b>개 · 표에만 반영되고 [저장]을 눌러야 저장돼요.
              </p>
            </div>
            <div className="cp-modal-footer cp-evaluation-plan-bulk-import-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setImportState(null)}>
                취소
              </button>
              <button
                type="button"
                className={importState.mode === 'replace' && hasExistingContent ? 'cp-btn-danger' : 'cp-btn-primary'}
                disabled={importSelectedRows.length === 0}
                onClick={applyImport}
              >
                {importState.mode === 'replace' && hasExistingContent ? '교체하기' : '불러오기'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <AutoStatusToast state={toastState} className="cp-evaluation-plan-bulk-toast" />
    </div>
  );
};

export default EvaluationStandardBulkEditModal;
